/* eslint-disable @reatom/unit-naming-rule */

import { useAtom } from '@reatom/npm-react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { styled } from 'styled-components'

import { AsyncState } from '@blade/std-lib'
import { Tooltip } from '@blade/ui-kit'
import { delugeIdAtom } from '@root/entities/auth/atoms'
import { ProviderIcon } from '@root/entities/provider/ui'
import { CurrencyIconPopover } from '@root/features/currency/ui'
import { PairPopoverByAddress } from '@root/features/pair'
import { TradeRoute } from '@root/features/routing'
import { reatomImage, useOptionalAtom } from '@root/shared/lib'

import { reatomCurrencyIcon } from '../../atoms'
import { CurrencyModel } from '../../model'

import { CurrencyIconFallback } from './currency-icon-fallback'
import { CurrencyIconMinimal } from './currency-icon-minimal'
import { PairIconPremiumOutline } from './currency-icon-premium-outline'
import { CurrencyIconRestricted } from './currency-icon-restricted'
import { getStableCurrencyIconSrc } from './currency-icon-stable'
import { CurrencyNetworkIcon } from './currency-network-icon'
import { PairMigratedIcon } from './pair-migrated-icon'
import { PremiumLogoFallback } from './premium-logo-placeholder'
import { usePairIconSizes } from './use-currency-icon-sizes'

import type { ImageHash } from '../../model'
import type {
  PairAddress,
  PairMigrationStatus,
} from '@root/entities/pair/model'
import type { PremiumMetaModel } from '@root/entities/premium'
import type { Provider } from '@root/entities/provider'
import type { CurrencyIconPopoverProps } from '@root/features/currency/ui'
import type { ClassNameProps } from '@root/shared/react'
import type { TooltipCardProps } from '@root/shared/ui-kit'

// TODO: Too many circular imports here

type OutlineMode =
  | 'outline'
  | 'premium'
  | {
      type: 'custom'
      colorString: string
    }

type Shape = 'rounded' | 'circle'

type Props = {
  currency: CurrencyModel
  premiumInfo?: PremiumMetaModel | undefined
  size?: number
  auxIconSize?: number
  provider?: Provider.Provider
  withNetwork?: boolean
  withGoogleLens?: boolean
  withPremiumFallback?: boolean

  onPremiumFallbackClick?: () => void
  /**
   * @default true
   */
  withPreview?: boolean
  migration?: PairMigrationStatus.PairMigrationStatus
  outline?: OutlineMode
  shape?: Shape
  iconSrcOverride?: string | undefined
  bannerSrcOverride?: string | 'none' | undefined
  imageHashOverride?: ImageHash | 'none' | undefined
  reusedImages?: CurrencyIconPopoverProps['reusedImages']
  pairSide?: TooltipCardProps['side']
  pairAddress?: PairAddress
} & ClassNameProps &
  Pick<React.HTMLAttributes<HTMLElement>, 'onClick'>

export const CurrencyIcon: React.FC<Props> = React.memo(
  ({
    currency,
    size = 30,
    provider,
    auxIconSize: auxIconSizeOverride,
    withNetwork = false,
    withPremiumFallback = true,
    withGoogleLens: withGoogleLensProp = true,
    withPreview: withPreviewProp = true,
    migration,
    outline: outlineInitial,
    shape: shapeProp = 'rounded',
    iconSrcOverride,
    bannerSrcOverride,
    imageHashOverride,
    onPremiumFallbackClick,
    premiumInfo,
    reusedImages,
    pairAddress,
    pairSide = 'left',
    ...htmlProps
  }) => {
    const { t } = useTranslation()
    const [iconStatus] = useAtom(currency.iconStatus)
    const isNativeCurrency = useMemo(
      () => CurrencyModel.isNativeOrWrapped(currency.network)(currency),
      [currency],
    )

    const premium = useOptionalAtom(premiumInfo?.premium)
    const [shouldRenderPremiumFallbackIfNotFound] = useAtom(
      (ctx) => {
        if (!premiumInfo) {
          return true
        }
        const premium = ctx.spy(premiumInfo.premium)
        const delugeId = ctx.spy(delugeIdAtom)
        const owner = ctx.spy(premiumInfo.owner)

        if (!premium || !delugeId || !owner) {
          return true
        }
        return delugeId === owner.delugeId
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [delugeIdAtom, premiumInfo],
    )

    const model = useMemo(() => {
      return iconSrcOverride
        ? reatomImage({ src: iconSrcOverride })
        : reatomCurrencyIcon({ currency })
    }, [currency, iconSrcOverride])

    const stableIconSrc = useMemo(() => {
      return getStableCurrencyIconSrc(currency)
    }, [currency])

    const withPreview = stableIconSrc ? false : withPreviewProp
    const shape = stableIconSrc ? 'circle' : shapeProp
    const withGoogleLens = stableIconSrc ? false : withGoogleLensProp

    const [state] = useAtom(
      (ctx) => {
        if (stableIconSrc) {
          return AsyncState.success(stableIconSrc)
        }
        return ctx.spy(model)
      },
      [stableIconSrc, model],
    )

    const outline = isNativeCurrency ? false : outlineInitial
    const outlineColor =
      typeof outline === 'object' ? outline.colorString : undefined

    const {
      auxIconSize,
      padding,
      premiumFallbackSize,
      outerRadius,
      innerRadius,
      auxIconOffset,
      fallbackFontSize,
    } = usePairIconSizes({
      size,
      outlined: !!outline,
    })

    const resolvedAuxIconSize = auxIconSizeOverride ?? auxIconSize
    const style = useMemo(() => {
      return {
        '--currency-icon-size': `${size}px`,
        '--currency-icon-border-radius':
          shape === 'rounded' ? `${outerRadius}px` : '50%',
        '--currency-icon-inner-border-radius':
          shape === 'rounded' ? `${innerRadius}px` : '50%',
        '--currency-icon-padding': `${padding}px`,
        '--currency-icon-aux-icon-offset':
          shape === 'rounded' ? `${auxIconOffset}px` : 0,
        '--currency-icon-outline-color': outlineColor,
        fontSize: `${fallbackFontSize}px`,
      } as React.CSSProperties
    }, [
      size,
      shape,
      outerRadius,
      innerRadius,
      padding,
      auxIconOffset,
      outlineColor,
      fallbackFontSize,
    ])

    const renderRightAuxIcon = () => {
      if (isNativeCurrency) {
        return undefined
      }

      if (withNetwork) {
        return (
          <NetworkAux size={resolvedAuxIconSize} network={currency.network} />
        )
      }

      if (!provider) {
        return undefined
      }

      return (
        <ProviderAux
          provider={provider}
          size={resolvedAuxIconSize}
          withTooltip
        />
      )
    }

    const renderLeftAuxIcon = () => {
      if (isNativeCurrency) {
        return undefined
      }

      if (migration) {
        return (
          <PairMigratedIcon size={resolvedAuxIconSize} migration={migration} />
        )
      }
    }

    const renderIcon = ({
      withPreview: withPreviewOverride = withPreview,
    }: {
      withPreview?: boolean
    } = {}) => {
      if (isNativeCurrency) {
        return <CurrencyNetworkIcon size={size} network={currency.network} />
      }

      if (iconStatus === 'restricted') {
        return (
          <Tooltip content={t('currency.logo.restricted-tooltip')}>
            <RestrictedIcon iconSize={size} />
          </Tooltip>
        )
      }

      const iconEl = (
        <Icon
          state={state}
          currency={currency}
          renderNotFound={
            withPremiumFallback && shouldRenderPremiumFallbackIfNotFound
              ? () => (
                  <PremiumFallback
                    currency={currency}
                    size={premiumFallbackSize}
                    padding={premiumFallbackSize > 50 ? 4 : 2}
                    onClickOverride={onPremiumFallbackClick}
                  />
                )
              : () => (
                  <CurrencyIconFallback
                    currency={currency}
                    premium={!!premium}
                  />
                )
          }
          withGoogleLens={withGoogleLens}
        />
      )

      if (!withPreviewOverride) {
        return iconEl
      }

      return (
        <CurrencyIconPopover
          premiumInfo={premiumInfo}
          currency={currency}
          iconSrcOverride={iconSrcOverride}
          bannerSrcOverride={bannerSrcOverride}
          imageHashOverride={imageHashOverride}
          reusedImages={reusedImages}
        >
          {iconEl}
        </CurrencyIconPopover>
      )
    }

    const resolvedWithPreview =
      pairAddress && !reusedImages?.side ? false : withPreview

    const tradeLink = useMemo(() => {
      if (!pairAddress || withGoogleLens) {
        return undefined
      }
      return TradeRoute.build({
        pairAddress,
        network: currency.network,
      })
    }, [pairAddress, withGoogleLens, currency.network])

    const icon = (
      <Root
        style={style}
        data-outline={typeof outline === 'object' ? outline.type : outline}
        data-restricted={iconStatus !== 'active'}
        data-shape={shape}
        {...htmlProps}
        {...(tradeLink
          ? {
              as: Link,
              to: tradeLink,
              onClick: (e: React.MouseEvent<HTMLAnchorElement>) => {
                e.stopPropagation()
                htmlProps.onClick?.(
                  e as unknown as React.MouseEvent<HTMLDivElement>,
                )
              },
            }
          : undefined)}
      >
        {outline === 'premium' ? (
          <PairIconPremiumOutline
            size={size}
            radius={shape === 'rounded' ? outerRadius : size / 2}
          />
        ) : undefined}
        {renderIcon({ withPreview: resolvedWithPreview })}
        {renderLeftAuxIcon()}
        {renderRightAuxIcon()}
      </Root>
    )

    if (reusedImages?.side || !pairAddress) {
      return icon
    }

    return (
      <PairPopoverByAddress
        pairAddress={pairAddress}
        network={currency.network}
        side={pairSide}
      >
        {icon}
      </PairPopoverByAddress>
    )
  },
)

const Root = styled.div`
  pointer-events: auto;
  position: relative;
  display: block;
  width: var(--currency-icon-size);
  height: var(--currency-icon-size);
  padding: var(--currency-icon-padding);
  border-radius: var(--currency-icon-border-radius);
  transition: border-color 0.15s linear;

  &:link,
  &:visited {
    color: currentColor;
  }

  &[data-outline='outline'] {
    border: 1px solid var(--backgroundA-10);
    &[data-restricted='true'] {
      border: 1px solid var(--backgroundA-4);
    }
  }
  &[data-outline='custom'] {
    border: 1px solid var(--currency-icon-outline-color);
  }
  &[data-outline='premium'] {
    // For svg border
    padding: calc(var(--currency-icon-padding) + 1px);
  }
`

const NetworkAux = styled(CurrencyNetworkIcon)`
  position: absolute;
  right: var(--currency-icon-aux-icon-offset);
  bottom: var(--currency-icon-aux-icon-offset);
`

const ProviderAux = styled(ProviderIcon)`
  position: absolute;
  right: var(--currency-icon-aux-icon-offset);
  bottom: var(--currency-icon-aux-icon-offset);
`

const PremiumFallback = styled(PremiumLogoFallback)`
  border-radius: var(--currency-icon-inner-border-radius);
`

const Icon = styled(CurrencyIconMinimal)`
  border-radius: var(--currency-icon-inner-border-radius);
`

const RestrictedIcon = styled(CurrencyIconRestricted)`
  border-radius: var(--currency-icon-inner-border-radius);
`
